import { ref, nextTick } from 'vue';
import { useStore } from 'dashboard/composables/store';

export function useKanbanDragAndDrop({
  columns,
  board,
  tickets,
  updateColumnTickets,
  updateColumnsTickets,
}) {
  const store = useStore();
  const draggedTicketId = ref(null);
  const isDragging = ref(false);
  const draggedFromColumnId = ref(null);
  const isUpdatingFromDrag = ref(false);
  const draggedToColumnId = ref(null);
  const lastMousePosition = ref({ x: 0, y: 0 });

  const findColumnAtPoint = (x, y) => {
    const element = document.elementFromPoint(x, y);
    if (!element) return null;

    let currentElement = element;
    let attempts = 0;
    const maxAttempts = 15;

    while (currentElement && attempts < maxAttempts) {
      if (
        currentElement.classList &&
        currentElement.classList.contains('column-content')
      ) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId) return colId;
      }

      if (
        currentElement.classList &&
        currentElement.classList.contains('kanban-column')
      ) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId) return colId;
      }

      if (currentElement.getAttribute) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId) return colId;
      }

      currentElement = currentElement.parentElement;
      attempts++;
    }

    return null;
  };

  // Rastrear posição do mouse durante o drag
  const handleMouseMove = event => {
    if (isDragging.value) {
      lastMousePosition.value = { x: event.clientX, y: event.clientY };
    }
  };

  // Adicionar listener de mouse quando o drag inicia
  const handleDragStart = event => {
    isDragging.value = true;
    draggedTicketId.value = parseInt(event.item.dataset.ticketId, 10);
    draggedToColumnId.value = null; // Reset

    // Adicionar listener global de mouse
    document.addEventListener('mousemove', handleMouseMove);

    // Encontrar a coluna de origem
    const from = event.from;
    let fromColumnContent = from?.closest('.column-content');
    if (!fromColumnContent && from) {
      let current = from.parentElement;
      while (current && !fromColumnContent) {
        if (current.classList && current.classList.contains('column-content')) {
          fromColumnContent = current;
          break;
        }
        if (current.getAttribute && current.getAttribute('data-column-id')) {
          fromColumnContent = current;
          break;
        }
        current = current.parentElement;
      }
    }

    if (fromColumnContent) {
      draggedFromColumnId.value =
        fromColumnContent.getAttribute('data-column-id');
    }

    // Salvar posição inicial do mouse
    if (event.originalEvent) {
      lastMousePosition.value = {
        x: event.originalEvent.clientX,
        y: event.originalEvent.clientY,
      };
    }
  };

  const handleDragAdd = (event, columnId) => {
    // Quando um item é adicionado a uma lista, essa é a coluna de destino
    if (isDragging.value && draggedTicketId.value) {
      draggedToColumnId.value = columnId.toString();
    }
  };

  const handleColumnChange = (event, columnId) => {
    // O evento @change é disparado quando há mudanças na lista
    // Se há um item adicionado (added) e estamos arrastando, essa é a coluna de destino
    if (event.added && event.added.element && isDragging.value) {
      const addedTicketId = event.added.element.id;
      if (addedTicketId === draggedTicketId.value) {
        draggedToColumnId.value = columnId.toString();
      }
    }
  };

  const handleTicketMove = async event => {
    const { item, to, from } = event;

    if (!item || !to) {
      resetDragState();
      return;
    }

    const ticketId = parseInt(item.dataset.ticketId, 10);
    if (!ticketId) {
      resetDragState();
      return;
    }

    isUpdatingFromDrag.value = true;

    let targetColumnId = null;

    if (draggedToColumnId.value) {
      targetColumnId = draggedToColumnId.value;
    }

    document.removeEventListener('mousemove', handleMouseMove);

    if (!targetColumnId && to) {
      targetColumnId = findColumnIdInElement(to);
    }

    if (!targetColumnId && item) {
      await nextTick();
      targetColumnId = findColumnIdInElement(item);
    }

    if (!targetColumnId) {
      const colId = findColumnAtPoint(
        lastMousePosition.value.x,
        lastMousePosition.value.y
      );
      if (colId && colId !== draggedFromColumnId.value) {
        targetColumnId = colId;
      }
    }

    if (!targetColumnId && event.originalEvent) {
      const mouseEvent = event.originalEvent;
      const colId = findColumnAtPoint(mouseEvent.clientX, mouseEvent.clientY);
      if (colId && colId !== draggedFromColumnId.value) {
        targetColumnId = colId;
      }
    }

    if (!targetColumnId) {
      await new Promise(resolve => setTimeout(resolve, 100));
      targetColumnId = findColumnIdByTicket(ticketId);
    }

    if (!targetColumnId) {
      resetDragState();
      return;
    }

    // Usar a coluna de origem que foi salva no drag start
    const sourceColumnId = draggedFromColumnId.value;

    // Se a origem e destino são iguais, não fazer nada
    if (sourceColumnId === targetColumnId || !sourceColumnId) {
      resetDragState();
      return;
    }

    const targetColumn = columns.value.find(
      col => col.id.toString() === targetColumnId
    );

    if (!targetColumn || !board.value) {
      resetDragState();
      return;
    }

    const ticket = tickets.value.find(t => t.id === ticketId);
    if (!ticket) {
      resetDragState();
      return;
    }

    try {
      // Atualizar via API (vuedraggable já moveu o item na UI)
      await store.dispatch('tickets/changeBoard', {
        id: ticketId,
        boardId: board.value.id,
        boardColumnId: targetColumn.id,
      });

      // Atualizar apenas as duas colunas afetadas em vez de reconstruir todo o quadro
      if (updateColumnsTickets) {
        updateColumnsTickets([sourceColumnId, targetColumnId]);
      } else {
        updateColumnTickets();
      }
    } catch (error) {
      console.error('Erro ao mover ticket:', error);
      // Recarregar para reverter visualmente em caso de erro
      await store.dispatch('tickets/getTickets');
      updateColumnTickets();
    } finally {
      resetDragState();
    }
  };

  const findColumnIdInElement = element => {
    let currentElement = element;
    let attempts = 0;
    const maxAttempts = 10;

    while (currentElement && attempts < maxAttempts) {
      if (
        currentElement.getAttribute &&
        currentElement.getAttribute('data-column-id')
      ) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId && colId !== draggedFromColumnId.value) {
          return colId;
        }
      }

      if (
        currentElement.classList &&
        currentElement.classList.contains('column-content')
      ) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId && colId !== draggedFromColumnId.value) {
          return colId;
        }
      }

      if (
        currentElement.classList &&
        currentElement.classList.contains('kanban-column')
      ) {
        const colId = currentElement.getAttribute('data-column-id');
        if (colId && colId !== draggedFromColumnId.value) {
          return colId;
        }
      }

      currentElement = currentElement.parentElement;
      attempts++;
    }

    return null;
  };

  const findColumnIdByTicket = ticketId => {
    // This is a fallback method, but we should have targetColumnId by now
    // If we reach here, something went wrong with the drag detection
    return null;
  };

  const resetDragState = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    draggedTicketId.value = null;
    isDragging.value = false;
    draggedFromColumnId.value = null;
    draggedToColumnId.value = null;
    isUpdatingFromDrag.value = false;
  };

  return {
    draggedTicketId,
    isDragging,
    isUpdatingFromDrag,
    handleDragStart,
    handleDragAdd,
    handleColumnChange,
    handleTicketMove,
  };
}

